import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

/**
 * Matches every portaled chart popup (settings modal, indicator/overlay
 * drawers, layout popover, context menu, select menus, line-style popover) plus
 * third-party Radix poppers we can't mark (sync tooltips, the drill's
 * ticker-retarget search dropdown). Chart popups portal to `<body>`, so a host
 * that closes on an outside mousedown (e.g. the drill click-away in
 * multi-chart-panel) must ignore clicks landing inside one — otherwise opening
 * a popup and clicking it dismisses the surface that opened it.
 */
export const CHART_POPUP_SELECTOR =
  '[data-chart-popup], [data-chart-select-menu], [data-line-style-popover], [data-radix-popper-content-wrapper]'

/**
 * Dismiss a portaled chart panel on Escape or an outside click — the shared
 * dismissal shared by the settings modal, the indicator + overlay settings
 * drawers, the price-scale menu, and the layout popover.
 *
 * Both listeners are CAPTURE-phase:
 *  - **Escape** is consumed (`preventDefault` + `stopPropagation`) so it doesn't
 *    also reach the workspace's bubble-phase handlers (fullscreen collapse).
 *  - The **outside-click** is deferred one tick so the click that OPENED the
 *    panel doesn't immediately re-close it.
 *
 * A click inside `ref`, on a nested Radix popper (host color pickers / tooltips
 * render outside `ref`), or matching `guardSelector` (e.g. the trigger button's
 * `[data-action="…"]`) is ignored. `onDismiss` is read through a ref so the
 * listeners aren't re-registered when the caller passes a fresh closure.
 */
export function usePortalDismiss(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  opts?: { enabled?: boolean; guardSelector?: string },
): void {
  const enabled = opts?.enabled ?? true
  const guardSelector = opts?.guardSelector
  const dismissRef = useRef(onDismiss)
  useLayoutEffect(() => {
    dismissRef.current = onDismiss
  })

  useEffect(() => {
    if (!enabled) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      dismissRef.current()
    }
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!ref.current || ref.current.contains(target as Node)) return
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) return
      // App-styled select menus portal to <body> (see DrawerSelect) — a click
      // inside one must not dismiss the panel that opened it.
      if (target?.closest?.('[data-chart-select-menu]')) return
      // The consolidated line-style editor's popover also portals to <body>.
      if (target?.closest?.('[data-line-style-popover]')) return
      if (guardSelector && target?.closest?.(guardSelector)) return
      dismissRef.current()
    }

    document.addEventListener('keydown', onKey, true)
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown, true), 0)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      clearTimeout(timer)
      document.removeEventListener('mousedown', onDown, true)
    }
  }, [ref, enabled, guardSelector])
}
